import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { Column } from 'primereact/column';
import { Button } from '../../../components/ui/Button';

import { useTitle, formatDuration } from '../../../config/global';
import DeleteConfirmation from '../../SharedComponents/DeleteConfirmation';
import UpdateCampaign from '../Components/Update';
import AssignPopup from '../Components/AssignToDevicePopup';
import Layout from "../../Layout/Layout";
import Create from '../Components/Create';

import { toggleDialog, setIsPrayerCampaign, toggleDeleteDialog, setCampaignDetails } from '../../../redux/Slices/Global/globalSlice';
import { useDeleteRowMutation } from '../../../queries/dataTable';
import { useForm } from 'react-hook-form';
import { useCampaignUsage, useGetAllCampaigns, useUpdateCampaignMutation } from '../../../queries';
import { isEmpty } from 'lodash';
import { InputText } from 'primereact/inputtext';
import { setPreviewMode } from '../../../redux/Slices/campaignSlice';
import { ProgressSpinner } from 'primereact/progressspinner';
import CampaignSettings from '../Components/setup/component/settings/CampaignSettings';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const CampaignDatatable = () => {
    useTitle('Interactive Campaigns');
    const { t } = useTranslation();
    const { control, formState: { errors }, setValue } = useForm();

    const { openDialog, deleteDialog } = useSelector((state) => state.global);
    const { previewMode } = useSelector((state) => state.campaign);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [editingCampaignId, setEditingCampaignId] = useState(null);
    const [titleValue, setTitleValue] = useState("");
    const updateCampaign = useUpdateCampaignMutation();

    const [campaign, setCampaign] = useState({ name: "" });
    const [campaignName, setCampaignName] = useState();
    const [campaignUsage, setCampaignUsage] = useState();


    const [campaignId, setCampaignId] = useState();
    const deleteCampaign = useDeleteRowMutation();

    const {
        data,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch
    } = useGetAllCampaigns({
        per_page: 25,
        search: searchTerm.trim() || undefined
    });


    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);


    const [loadingAll, setLoadingAll] = React.useState(false);

    const loadAll = React.useCallback(async () => {
        if (loadingAll || isLoading) return;
        setLoadingAll(true);

        try {
            // Use the server’s own pagination info to know how many more to grab
            // Take the last page you have, figure out remaining pages, loop.
            let last = data?.pages?.[data.pages.length - 1];
            let remaining = last ? (last.totalPages - last.currentPage) : 0;

            // Fallback if we don't have data yet: just loop while hasNextPage flips true
            if (!last) {
                while (hasNextPage) {
                    await fetchNextPage();
                }
            } else {
                for (let i = 0; i < remaining; i++) {
                    await fetchNextPage();
                }
            }
        } finally {
            setLoadingAll(false);
        }
    }, [data, fetchNextPage, hasNextPage, isLoading, loadingAll]);

    useEffect(() => {
        if (!sentinelRef.current) return;

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: scrollRef.current ?? null,  // observe the real scroller
                rootMargin: '600px 0px 400px 0px', // prefetch before bottom
                threshold: 0,
            }
        );

        io.observe(sentinelRef.current);
        return () => io.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);


    const inputRefs = useRef({});
    const handleInput = () => setSearchTerm(searchTerm.trim());
    const resetInput = () => setSearchTerm("");
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleInput();
        }
    };


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                editingCampaignId &&
                inputRefs.current[editingCampaignId] &&
                !inputRefs.current[editingCampaignId].contains(e.target)
            ) {
                setEditingCampaignId(null);
                setTitleValue("");
            }
        };

        if (editingCampaignId) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [editingCampaignId]);

    const createCampaign = () => {
        setCampaign({});
        // setIsInteractiveCampaign(1)
        dispatch(toggleDialog("createCampaign"));
    }


    const subNavbarContent = useMemo(() => {
        return (
            <div className={`flex justify-end items-center w-100`}>
                <div className="flex-grow-1 me-2">
                    <div className="relative flex w-full justify-start pb-2 pt-2 px-3 ">
                        <input
                            type="text"
                            className="form-control h-[45px] w-8/12 md:w-10/12 hover:border-blue-500"
                            placeholder={t('common.search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyPress}
                        // disabled={isLoading}
                        />
                        {!isEmpty(searchTerm) && (
                            <Button
                                icon="pi pi-times"
                                rounded
                                text
                                severity="primery"
                                aria-label={t('buttons.cancel')}
                                onClick={resetInput}
                                className="absolute inset-y-0 right-10 border-transparent bg-transparent text-black"
                            />
                        )}
                        <Button
                            icon="pi pi-search"
                            rounded
                            text
                            severity="primery"
                            aria-label={t('buttons.search')}
                            className="absolute inset-y-0 right-2 border-transparent bg-transparent text-black"
                            onClick={handleInput}
                            loading={isLoading}
                        />
                    </div>

                </div>
                <Button className="p-2 p-button-sm h-[45px]" icon="fa-solid fa-bullhorn" label={t('show.show_create_campaign')}
                    onClick={createCampaign}
                />
            </div>
        )
    }, [searchTerm, isLoading]);



    const handleSubmitTag = async (item, titleValue, key) => {

        await updateCampaign.mutateAsync({
            id: item.id,
            data: {
                "status": item.status,
                "name": titleValue,
            },
        }, {
            onSuccess: () => {
                // showToast('success', 'Campaign Updating', "The campaign updated successfully!");
                refetch()
                setEditingCampaignId(null);
                setTitleValue("");
            }
        })

        // await resetCampaign.mutateAsync({ id, name: campaignInfo?.name })
    }
    const totalCount = data?.pages[0]?.totalItems ?? 0;
    const campaigns = data?.pages.flatMap((page) => page.items) ?? [];
    const assignHandler = (id, name) => {
        setCampaignId(id)
        setCampaignName(name)
        dispatch(toggleDialog("assignDeviceGroup"))
    }

    const coverHandler = (rowData) => {
        setCampaignId(rowData.id)
        setCampaignName(rowData.name)
        dispatch(setCampaignDetails(rowData))
        dispatch(toggleDialog('campaignSettings'))
    }

    const editCampaign = (data) => {
        setCampaign(data);
        dispatch(toggleDialog("updateCampaign"))
    }
    //Delete Campaign Handler
    const confirmDeleteUser = (data) => {
        setCampaign(data);
        dispatch(toggleDeleteDialog("interactiveCampaign"));
    }

    const goToSetup = (campaignID) => {
        sessionStorage.setItem('is_default_prayer', JSON.stringify(false));
        dispatch(setIsPrayerCampaign(false))
        navigate(`/interactive/campaign/${campaignID}/setup`)
    }

    const deleteCampaignHandler = async () => {
        await deleteCampaign.mutateAsync({
            id: campaign?.id,
            table: 'campaign'
        }, {
            onSuccess: () => {
                refetch()
                dispatch(toggleDeleteDialog("interactiveCampaign"))
            }
        })
    }

    const actionBodyTemplate = (rowData) => {
        const isCampaignEmpty = rowData.campaign_duration === 0;

        return (
            <div className="flex gap-2 text-nowrap ">
                {/* {previewMode === "grid" && ( */}
                <>
                    {/* Assign to device */}
                    < Tooltip target={`.cover-button-${rowData.id}`} showDelay={100} className="fs-8" />
                    <button className={`btn btn-sm btn-icon cover-button-${rowData.id}`}
                        data-pr-position="bottom"
                        data-pr-tooltip={t('show.show_edit_cover_named', { name: rowData.name })}
                        onClick={() => { coverHandler(rowData) }} >
                        <i className='bx bx-cog'></i>
                    </button>
                </>
                {/* )} */}



                {/* Assign to device */}
                <Tooltip target={`.assign-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm btn-icon assign-button-${rowData.id}`}
                    data-pr-position="bottom"
                    data-pr-tooltip={isCampaignEmpty ? t('show.show_main_layer_empty') : t('show.show_assign_to_device')}
                    onClick={() => { !isCampaignEmpty && assignHandler(rowData.id, rowData.name) }} >
                    <i className='bx bx-up-arrow-alt'></i>
                </button>

                {/* Link to setup page */}
                {/* <Tooltip target={`.setup-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm btn-icon setup-button-${rowData.id}`}
                    onClick={() => goToSetup(rowData.id)} data-pr-position="bottom" data-pr-tooltip="Add Content"><i className='bx bx-slideshow' ></i></button> */}

                {/* Update campaign */}
                {/* <Tooltip target={`.update-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm btn-icon update-button-${rowData.id}`} data-pr-position="bottom" data-pr-tooltip="
                    Update" onClick={() => editCampaign(rowData)}><i className="pi pi-pencil"></i></button> */}

                {
                    rowData.name != "default" ?
                        <>
                            {/* Delete Campaign */}
                            <Tooltip target={`.delete-button-${rowData.id}`} showDelay={100} className="fs-8" />
                            <button className={`btn btn-sm btn-icon delete-record delete-button-${rowData.id}`} data-pr-position="bottom" data-pr-tooltip="Delete" onClick={() => confirmDeleteUser(rowData)} ><i className="bx bx-trash text-red-600"></i></button>
                        </>
                        :
                        <>
                        </>
                }
            </div>
        );
    }
    return (
        <Layout subNavbarContent={subNavbarContent}>
            <div className="card h-100 w-100 flex flex-col gap-3 p-3">
                <div className="flex justify-between">
                    <small
                        onClick={loadAll}
                        className={[
                            "bg-gray-100 px-2 py-1 rounded cursor-pointer select-none",
                            (loadingAll || isLoading || isFetchingNextPage || !hasNextPage) ? "opacity-60 cursor-not-allowed" : ""
                        ].join(" ")}
                        title={t('show.show_fetch_all_pages')}
                    >
                        {t('common.all')}&nbsp;
                        {(isLoading || loadingAll || isFetchingNextPage) ? "..." : (data?.pages?.[0]?.totalItems ?? totalCount ?? 0)}
                    </small>
                    <div className="flex gap-1">
                        {(isFetchingNextPage || (searchTerm && isLoading)) && (
                            <div className="flex justify-end items-center h-full">
                                <ProgressSpinner style={{ width: "30px", height: "30px" }} />
                            </div>
                        )}
                        <div className="flex items-center">
                            <i className={`pi pi-microsoft fs-5 mx-3 cursor-pointer ${previewMode === "grid" ? "" : "text-[#CCCCCE]"}`} onClick={() => dispatch(setPreviewMode("grid"))}></i>
                            <i className={`pi pi-list fs-4 mx-3  cursor-pointer ${previewMode === "list" ? "" : "text-[#CCCCCE]"}`} onClick={() => dispatch(setPreviewMode("list"))}></i>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                    </div>
                ) : campaigns.length === 0 ? (
                    <h5 className="py-5 text-center w-full">
                        {isEmpty(searchTerm) ? "" : t('messages.no_results')}
                    </h5>
                ) : (


                    <div ref={scrollRef} style={{ maxHeight: 'calc(100vh - 220px)' }}
                        className={`  d-flex flex-column   scroll_div  overflow-x-hidden  w-100`}>
                        {previewMode === "list" ?
                            <div className="flex gap-3 flex-wrap mb-4">
                                {
                                    campaigns?.map((item, index) => {
                                        const oldUrl1 = "http://storage-popuppush-copy.s3.eu-west-1.amazonaws.com";
                                        const oldUrl2 = "http://storage-popuppush.s3.me-south-1.amazonaws.com";
                                        const newUrl = "https://storage.popuppush.com";

                                        const normalizeCdn = (url) => {
                                            if (!url) return url;
                                            if (url.includes(oldUrl1)) return url.replace(oldUrl1, newUrl);
                                            if (url.includes(oldUrl2)) return url.replace(oldUrl2, newUrl);
                                            return url;
                                        };

                                        // Prefer an explicit thumbnail, then thumb, then original path
                                        const rawThumb =
                                            item?.cover?.image_thumb ??
                                            item?.cover?.image_path ??
                                            null;

                                        let thumb = null;
                                        if (rawThumb === "loading") {
                                            thumb = "loader";
                                        } else if (rawThumb) {
                                            thumb = normalizeCdn(rawThumb);
                                        }

                                        return (

                                            <div className="group border rounded-xl w-100 flex  items-center h-[85px]   p-2 "
                                                // ref={index === campaigns?.length - 1 ? observerRef : null}
                                                // ref={scrollRef}
                                                key={`${item.type} - ${index}`}

                                            >

                                                <div className={` col-lg-1 px-3 flex justify-center h-100 cursor-pointer`}
                                                    onClick={() => { goToSetup(item.id) }}

                                                >
                                                    {item.cover ?
                                                        item.cover.type === "COLOR" ?
                                                            <div className="w-[50px] h-[68px] rounded-xl"
                                                                style={{ backgroundColor: item.cover?.color || "#ccc" }}
                                                            > </div>

                                                            :
                                                            <div className="  w-[50px] h-[68px]">
                                                                <LazyLoadImage src={thumb}
                                                                    className={` z-10 w-full h-full object-cover rounded-lg border`}
                                                                    alt="item"
                                                                    onError={(e) => {
                                                                        // graceful fallback on broken URLs
                                                                        e.target.onerror = null;
                                                                        e.target.src =
                                                                            "data:image/svg+xml;charset=utf-8," +
                                                                            encodeURIComponent(
                                                                                '<svg xmlns="http://www.w3.org/2000/svg" width="50" height="68"><rect width="100%" height="100%" fill="#eee"/></svg>'
                                                                            );
                                                                    }}
                                                                />

                                                            </div>


                                                        :

                                                        <div className={`w-[50px] h-[68px] rounded-xl ${item.has_unpublished_changes !== true ? "bg-[#a9e2ee]" : "bg-[#7c95e7]"} `}></div>
                                                    }
                                                </div>
                                                <div   className={` font-[400px] col-2 overflow-hidden col-lg-2 col-sm-4 mx-1 fs-6 flex-col flex justify-start items-start  cursor-pointer     `}
                                                    onClick={() => {
                                                        //
                                                    }}>

                                                    <div className=" w-100 " ref={(el) => (inputRefs.current[item.id] = el)} >
                                                        {editingCampaignId === item.id ? (
                                                            <div className="relative w-100 flex items-center"  >
                                                                <InputText
                                                                    value={titleValue}
                                                                    onChange={(e) => setTitleValue(e.target.value)}
                                                                    autoFocus
                                                                    maxLength={30}
                                                                    placeholder={t('show.show_enter_title')}
                                                                    className="w-100 pr-8 titleCampaignIndexEditor focus:border-blue-500 outline-none bg-transparent shadow-none text-sm p-0 transition-all"
                                                                />
                                                                <Button
                                                                    icon="pi pi-check"
                                                                    className="absolute right-0 p-button-text p-button-sm"
                                                                    loading={updateCampaign.isLoading && updateCampaign.variables?.id === item.id}
                                                                    onClick={() =>
                                                                        handleSubmitTag(item, titleValue, true)
                                                                    }
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="cursor-pointer group/title flex items-center"
                                                                onClick={() => {
                                                                    setEditingCampaignId(item.id);
                                                                    setTitleValue(item.name);
                                                                }}
                                                            >
                                                                {item.name}
                                                                {updateCampaign.isLoading && updateCampaign.variables?.id === item.id ? (
                                                                    <i className="pi pi-spin pi-spinner ml-3" style={{ fontSize: "1rem" }}></i>
                                                                ) : (
                                                                    <i className="pl-3 fa-solid fa-pen fa-xs opacity-0  group-hover/title:opacity-100 transition-opacity duration-150"></i>
                                                                )}
                                                            </div>
                                                        )}


                                                    </div>
                                                    {item.has_unpublished_changes === true && (
                                                        <div className="gap-1 text-muted text-[12px] flex justify-start items-center" onClick={() => { goToSetup(item.id) }}>
                                                            <i
                                                                className={` draftedItem-${item.id} cursor-default fa-solid fa-circle-exclamation  text-[#f59e0b] `}
                                                                data-pr-position="bottom"
                                                                data-pr-tooltip={t('show.show_draft_tooltip')}
                                                            ></i>
                                                            <div
                                                                className={`draftedItem-${item.id} cursor-default  text-[#f59e0b]`}
                                                                data-pr-position="bottom"
                                                                data-pr-tooltip={t('show.show_draft_tooltip')}
                                                            >
                                                                {t('show.show_publish_required')}
                                                            </div>
                                                            <Tooltip target={`.draftedItem-${item.id}`} showDelay={100} className="fs-8" />
                                                        </div>
                                                    )}

                                                </div>

                                                <div className="col-lg-7 flex flex-col justify-center items-center cursor-pointer  "
                                                    onClick={() => { goToSetup(item.id) }}
                                                >
                                                    <div className="text-muted text-xs ">{t('show.show_duration')}</div>
                                                    <div className="">{formatDuration(item.campaign_duration)}</div>
                                                </div>


                                                <div className="col-lg-2  col-sm-5 flex justify-end px-4 opacity-0 pointer-events-none transition-opacity duration-150
                                                             group-hover:opacity-100 group-hover:pointer-events-auto
                                                             focus-within:opacity-100">
                                                    {actionBodyTemplate(item)}
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>

                            :
                            < div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                                {
                                    campaigns?.map((item, index) => {
                                        const oldUrl1 = "http://storage-popuppush-copy.s3.eu-west-1.amazonaws.com";
                                        const oldUrl2 = "http://storage-popuppush.s3.me-south-1.amazonaws.com";
                                        const newUrl = "https://storage.popuppush.com";

                                        const normalizeCdn = (url) => {
                                            if (!url) return url;
                                            if (url.includes(oldUrl1)) return url.replace(oldUrl1, newUrl);
                                            if (url.includes(oldUrl2)) return url.replace(oldUrl2, newUrl);
                                            return url;
                                        };

                                        const rawThumb =
                                            item?.cover?.image_thumb ??
                                            item?.cover?.image_path ??
                                            null;

                                        let thumb = null;
                                        if (rawThumb === "loading") {
                                            thumb = "loader";
                                        } else if (rawThumb) {
                                            thumb = normalizeCdn(rawThumb);
                                        }

                                        // has_unpublished_changes, not item.status — see the list-view badge above.
                                        const hasChanges = item.has_unpublished_changes === true;

                                        return (
                                            <div
                                                // ref={scrollRef}
                                                // ref={index === campaigns?.length - 1 ? observerRef : null}
                                                key={`${item.type} - ${index}`}
                                                className="group relative overflow-hidden rounded-2xl shadow-sm border border-transparent hover:border-gray-300 transition-all duration-200 aspect-[3/4] bg-[#0b1320]/5"


                                            >
                                                {/* COVER (image or color) */}
                                                {item.cover ? (
                                                    item.cover.type === "COLOR" ? (
                                                        <div
                                                            className="absolute inset-0 cursor-pointer"
                                                            style={{ background: item.cover?.color || "#ccc" }}
                                                            onClick={() => { goToSetup(item.id) }}
                                                        />
                                                    ) : (
                                                        <div className="absolute inset-0 cursor-pointer"
                                                            onClick={() => { goToSetup(item.id) }}
                                                        >
                                                            <LazyLoadImage
                                                                src={thumb}
                                                                alt="cover"
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src =
                                                                        "data:image/svg+xml;charset=utf-8," +
                                                                        encodeURIComponent(
                                                                            '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="100%" height="100%" fill="#eee"/></svg>'
                                                                        );
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                ) : (
                                                    <div
                                                        className={` cursor-pointer absolute inset-0 ${item.has_unpublished_changes !== true ? "bg-[#a9e2ee]" : "bg-[#7c95e7]"
                                                            }`}

                                                        onClick={() => { goToSetup(item.id) }}
                                                    />
                                                )}

                                                {/* Top gradient fade (like screenshot) */}
                                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-black/70" />

                                                {/* HOVER ACTIONS (top-right) */}
                                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none group-hover:pointer-events-auto">
                                                    {actionBodyTemplate?.(item)}
                                                </div>

                                                {/* Bottom content block */}
                                                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                                                    {/* Title (with inline edit) */}
                                                    <div
                                                        className="w-full"
                                                        ref={(el) => (inputRefs.current[item.id] = el)}
                                                    >
                                                        {editingCampaignId === item.id ? (
                                                            <div className="relative flex items-center">
                                                                <InputText
                                                                    value={titleValue}
                                                                    onChange={(e) => setTitleValue(e.target.value)}
                                                                    autoFocus
                                                                    maxLength={30}
                                                                    placeholder={t('show.show_enter_title')}
                                                                    className="w-full pr-9 titleCampaignIndexEditor focus:text-white bg-transparent text-sm focus:border-white/70 shadow-none outline-none"
                                                                />
                                                                <Button
                                                                    icon="pi pi-check"
                                                                    className="!absolute right-0 p-button-text p-button-sm"
                                                                    loading={
                                                                        updateCampaign.isLoading &&
                                                                        updateCampaign.variables?.id === item.id
                                                                    }
                                                                    onClick={() => handleSubmitTag(item, titleValue, true)}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="cursor-pointer group/title flex items-center text-[15px] font-medium drop-shadow"
                                                                onClick={() => {
                                                                    setEditingCampaignId(item.id);
                                                                    setTitleValue(item.name);
                                                                }}
                                                            >
                                                                {item.name}
                                                                {updateCampaign.isLoading &&
                                                                    updateCampaign.variables?.id === item.id ? (
                                                                    <i className="pi pi-spin pi-spinner ml-2 text-xs"></i>
                                                                ) : (
                                                                    <i className="pl-2 fa-solid fa-pen fa-xs opacity-0 group-hover/title:opacity-100 transition-opacity duration-150"></i>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Status line (Draft Saved. Publish required.) */}
                                                    {hasChanges && (
                                                        <div className="mt-2 flex items-center gap-2 text-[12px] text-white/90">
                                                            <i
                                                                className={`draftedItem-${item.id} fa-solid fa-circle-exclamation text-[#f59e0b]`}
                                                                data-pr-position="top"
                                                                data-pr-tooltip={t('show.show_draft_tooltip')}
                                                            />
                                                            <span
                                                                className={`draftedItem-${item.id} text-[#f59e0b]`}
                                                                data-pr-position="top"
                                                                data-pr-tooltip={t('show.show_draft_tooltip')}
                                                            >
                                                                {t('show.show_publish_required')}
                                                            </span>
                                                            <Tooltip
                                                                target={`.draftedItem-${item.id}`}
                                                                showDelay={100}
                                                                className="fs-8"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Minor meta row (like “Main”) + Duration */}
                                                    <div className="mt-2 flex items-center justify-between text-[14px] text-white/90">
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-block w-3 h-2 bg-white/90 rounded-sm" />
                                                            <span>{t('show.show_duration')}</span>
                                                        </div>
                                                        <div className="opacity-90">
                                                            {formatDuration(item.campaign_duration)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }

                            </div>
                        }

                        <div ref={sentinelRef} className="h-1 w-full" />
                    </div>

                )}

            </div>

            {openDialog.createCampaign ? <Create /> : <></>}
            {openDialog.updateCampaign ? <UpdateCampaign setReload={refetch()} campaign={campaign} /> : <></>}
            {openDialog.assignDeviceGroup ? <AssignPopup campaignId={campaignId} campaignName={campaignName} /> : <></>}
            {openDialog.campaignSettings ? <CampaignSettings showOption={false} campaignId={campaignId} /> : <></>}


            {
                deleteDialog.interactiveCampaign ?
                    <DeleteConfirmation
                        isLoading={deleteCampaign.isLoading}
                        deleteHandler={deleteCampaignHandler}
                        itemName={campaign.name}
                        dialogKey="interactiveCampaign"
                    />
                    : <></>
            }
        </Layout >
    );
}

export default CampaignDatatable;
